import { use } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import {
  QueryViewerContext,
  useDiscardUnavailableData,
} from "@softmax/web-features/data";

import { SweepButton } from "@softmax/design-system";
import { currentTosVersion } from "@/lib/tos";
import { accountProfileQueryOptions } from "../account.queries";
import type { AccountUser, ProfileSettingsData } from "../account.types";
import { useProfileSettings } from "./use-profile-settings";
import { useAccountSignOut } from "../settings/use-account-sign-out";
import { ProfileAbout } from "./sections/profile-about";
import { ProfileAgents } from "./sections/profile-agents";
import { ProfileDiscord } from "./sections/profile-discord";
import { ProfileGithub } from "./sections/profile-github";
import { ProfilePreferences } from "./sections/profile-preferences";
import { ProfileRecovery } from "./sections/profile-recovery";
import { ProfileSignOut } from "./sections/profile-sign-out";

type ProfileSettingsProps = ProfileSettingsData & { agents: PlayerResponse[] };

export function ProfileSettings(props: ProfileSettingsProps) {
  const viewerId = use(QueryViewerContext);
  const options = accountProfileQueryOptions({ viewerId });
  const profile = useQuery(options);
  const unavailable = useDiscardUnavailableData({
    queryKey: options.queryKey,
    error: profile.error,
  });

  if (unavailable || !profile.data) {
    return (
      <div
        role="alert"
        className="mb-[24px] flex flex-wrap items-center gap-[12px]"
      >
        <p className="font-plex-sans text-[16px] leading-[24px] text-[#5e5e5e]">
          Account settings are unavailable. {profile.error?.message}
        </p>
        <SweepButton
          quiet
          type="button"
          disabled={profile.isFetching}
          onClick={() => void profile.refetch()}
        >
          Retry account
        </SweepButton>
      </div>
    );
  }

  return (
    <ProfileSettingsEditor
      {...props}
      savedUser={profile.data}
      profileError={profile.error}
    />
  );
}

function ProfileSettingsEditor({
  discord,
  github,
  agents,
  savedUser,
  profileError,
}: ProfileSettingsProps & {
  savedUser: AccountUser;
  profileError: Error | null;
}) {
  const viewerId = use(QueryViewerContext);
  const editor = useProfileSettings({ savedUser, agents });
  const signOut = useAccountSignOut();
  const tosStale =
    savedUser.tosVersion !== null && savedUser.tosVersion !== currentTosVersion;
  return (
    <form onSubmit={(event) => event.preventDefault()}>
      <ProfileRecovery editor={editor} error={profileError} />
      <div className="flex flex-col gap-[48px] border-b border-[rgba(22,22,22,0.13)] pb-[64px] md:flex-row md:gap-[64px]">
        <ProfileAbout editor={editor} />
        <ProfileAgents editor={editor} />
      </div>
      <div className="flex flex-col gap-[48px] pb-[48px] md:flex-row md:gap-[64px]">
        <ProfilePreferences editor={editor} />
        <ProfileGithub github={github} userId={viewerId} />
      </div>
      <ProfileDiscord discord={discord} />
      <ProfileSignOut {...signOut} />
      {tosStale ? (
        <p className="font-plex-sans pb-[24px] text-[16px] leading-[24px] text-[#945637]">
          Your Terms of Service acceptance is out of date. Re-acceptance happens
          in the dedicated completion flow before protected areas load.
        </p>
      ) : null}
    </form>
  );
}
